import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import type { HubRiseCatalog, Selection } from '../types'

interface Props {
  catalog: HubRiseCatalog
  tab: 'products' | 'optionLists'
  onTabChange: (tab: 'products' | 'optionLists') => void
  selection: Selection
  onSelect: (sel: Selection) => void
  expandedCategories: Set<string>
  toggleCategory: (id: string) => void
  expandedProducts: Set<string>
  toggleProduct: (id: string) => void
}

const LeftNav = ({
  catalog,
  tab,
  onTabChange,
  selection,
  onSelect,
  expandedCategories,
  toggleCategory,
  expandedProducts,
  toggleProduct,
}: Props) => {
  const productsByCategory = new Map<string, typeof catalog.products>()
  for (const p of catalog.products) {
    const arr = productsByCategory.get(p.categoryId) ?? []
    arr.push(p)
    productsByCategory.set(p.categoryId, arr)
  }

  const isSelectedProduct = (productId: string) =>
    selection.kind === 'product' && selection.productId === productId
  const isSelectedSku = (skuId: string) =>
    selection.kind === 'sku' && selection.skuId === skuId
  const isSelectedList = (id: string) =>
    selection.kind === 'optionList' && selection.optionListId === id

  return (
    <div className="w-80 border-r border-secondary-200 bg-white flex flex-col">
      <div className="flex border-b border-secondary-200">
        <button
          type="button"
          onClick={() => onTabChange('products')}
          className={`flex-1 px-4 py-3 text-sm font-medium ${
            tab === 'products'
              ? 'border-b-2 border-primary-500 text-primary-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Products
        </button>
        <button
          type="button"
          onClick={() => onTabChange('optionLists')}
          className={`flex-1 px-4 py-3 text-sm font-medium ${
            tab === 'optionLists'
              ? 'border-b-2 border-primary-500 text-primary-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Option Lists
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === 'products' &&
          catalog.categories.map((cat) => {
            const products = productsByCategory.get(cat.id) ?? []
            const catExpanded = expandedCategories.has(cat.id)
            return (
              <div key={cat.id} className="border-b border-gray-100">
                <button
                  type="button"
                  onClick={() => toggleCategory(cat.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-gray-800 hover:bg-gray-50"
                >
                  {catExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="flex-1 truncate">{cat.name}</span>
                  <span className="text-xs text-gray-400">{products.length}</span>
                </button>
                {catExpanded &&
                  products.map((p) => {
                    const prodExpanded = expandedProducts.has(p.id)
                    return (
                      <div key={p.id}>
                        <div
                          className={`flex items-center gap-2 pl-8 pr-3 py-1.5 text-sm cursor-pointer ${
                            isSelectedProduct(p.id)
                              ? 'bg-primary-100 text-primary-900'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => onSelect({ kind: 'product', productId: p.id })}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleProduct(p.id)
                            }}
                            className="p-0.5"
                          >
                            {prodExpanded ? (
                              <ChevronDownIcon className="h-3 w-3 text-gray-400" />
                            ) : (
                              <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                            )}
                          </button>
                          <span className="flex-1 truncate">{p.name}</span>
                          <span className="text-[10px] uppercase tracking-wide text-green-700 bg-green-100 rounded px-1.5 py-0.5">
                            Live
                          </span>
                        </div>
                        {prodExpanded &&
                          p.skus.map((sku) => (
                            <div
                              key={sku.id}
                              className={`pl-14 pr-3 py-1 text-xs cursor-pointer truncate ${
                                isSelectedSku(sku.id)
                                  ? 'bg-primary-50 text-primary-800 font-medium'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                              onClick={() =>
                                onSelect({
                                  kind: 'sku',
                                  productId: p.id,
                                  skuId: sku.id,
                                })
                              }
                            >
                              {sku.name}
                            </div>
                          ))}
                      </div>
                    )
                  })}
              </div>
            )
          })}

        {tab === 'optionLists' &&
          catalog.optionLists.map((ol) => (
            <div
              key={ol.id}
              className={`px-3 py-2 text-sm cursor-pointer border-b border-gray-100 ${
                isSelectedList(ol.id)
                  ? 'bg-primary-100 text-primary-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => onSelect({ kind: 'optionList', optionListId: ol.id })}
            >
              <div className="flex items-center justify-between">
                <span className="truncate">{ol.name}</span>
                <span className="text-xs text-gray-400">{ol.options.length}</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}

export default LeftNav
